import React from 'react';

import { ActionCluster, SectionHeader } from '../components/AppShellPrimitives';
import { RuntimeRiskCoverageStatusPanel } from './RuntimeScenarioCandidateCard';

function formatDate(value) {
  if (!value) {
    return 'Not available';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(parsed);
}

function titleCase(value) {
  return String(value || '')
    .replaceAll('_', ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function buildHref(systemId, nextFilters) {
  const query = new URLSearchParams();
  if (nextFilters.workflowId) {
    query.set('workflow_id', nextFilters.workflowId);
  }
  if (nextFilters.findingStatus) {
    query.set('finding_status', nextFilters.findingStatus);
  }
  if (nextFilters.findingPriority) {
    query.set('finding_priority', nextFilters.findingPriority);
  }
  if (nextFilters.coverageStatus) {
    query.set('coverage_status', nextFilters.coverageStatus);
  }
  if (nextFilters.attentionOnly) {
    query.set('attention_only', 'true');
  }
  const queryString = query.toString();
  return `#/systems/${systemId}/runtime-coverage${queryString ? `?${queryString}` : ''}`;
}

function FilterSelect({ label, value, options, onChange, optionLabel = titleCase }) {
  return (
    <label className="filter-control">
      <span>{label}</span>
      <select value={value || ''} onChange={(event) => onChange(event.target.value || null)}>
        <option value="">All</option>
        {options.map((option) => (
          <option key={typeof option === 'string' ? option : option.id} value={typeof option === 'string' ? option : option.id}>
            {typeof option === 'string' ? optionLabel(option) : option.name}
          </option>
        ))}
      </select>
    </label>
  );
}

function proofReadiness(summary) {
  const statusCounts = summary?.status_counts || {};
  const activeCount = summary?.active_runtime_risk_count || 0;
  const importedCount = summary?.imported_summary_count || 0;
  const attentionCount = [
    'failing_eval',
    'stale',
    'needs_labels',
    'sent',
    'accepted_not_sent',
    'needs_more_evidence',
    'not_reviewed',
  ].reduce((total, status) => total + (statusCounts[status] || 0), 0);
  const coveredCount = ['passing_eval', 'covered_by_tests', 'reviewed_in_eval_tool'].reduce((total, status) => total + (statusCounts[status] || 0), 0);
  const proofCount = coveredCount || importedCount;

  if (!activeCount) {
    return {
      label: 'No active runtime risks',
      copy: 'Outcome proof starts after runtime risks are reviewed and evaluated.',
      next: 'Review new findings when runtime evidence changes.',
      tone: 'neutral',
    };
  }
  if (!importedCount) {
    return {
      label: 'Waiting for ASE outcomes',
      copy: 'Coverage has not become proof yet because no compact evaluation outcome summaries are attached.',
      next: 'Send accepted risks, generate or run evals in ASE, then import the compact outcome summary.',
      tone: 'attention',
    };
  }
  if (attentionCount) {
    return {
      label: 'Resolve coverage attention',
      copy: `${attentionCount} ${attentionCount === 1 ? 'risk still needs' : 'risks still need'} review, labels, fresh evaluation, or a passing result before proof is clean.`,
      next: 'Work through the attention lanes here, then compare runtime movement against validation proof.',
      tone: 'attention',
    };
  }
  return {
    label: 'Ready for proof compare',
    copy: `${proofCount} ${proofCount === 1 ? 'risk has' : 'risks have'} usable evaluation coverage for the current runtime evidence.`,
    next: 'Open Compare to review runtime movement, validation coverage, validation proof, and claim strength together.',
    tone: 'ready',
  };
}

function CoverageRow({ row }) {
  return (
    <article className="finding-card runtime-coverage-row">
      <div className="finding-card-main runtime-coverage-row-main">
        <div className="finding-card-header">
          <div>
            <p className="detail-kicker">{row.workflow_name || 'System-level risk'}</p>
            <h3>{row.finding_title}</h3>
          </div>
          <div className="finding-card-tags">
            <span className="chip chip-emphasis">{titleCase(row.priority)}</span>
            <span className="chip">{titleCase(row.severity)}</span>
            <span className="chip">{titleCase(row.finding_status)}</span>
          </div>
        </div>
        <p>{row.finding_summary}</p>
        {row.reviewed_scenario_label ? (
          <p className="muted-copy">Scenario review: {row.reviewed_scenario_label}</p>
        ) : null}
        <RuntimeRiskCoverageStatusPanel coverage={row.evaluation_coverage} compact />
        <ActionCluster className="runtime-coverage-row-actions">
          <a className="secondary-button action-link-button" href={row.finding_href}>
            Open finding
          </a>
          {row.workflow_href ? (
            <a className="secondary-button action-link-button" href={row.workflow_href}>
              Workflow
            </a>
          ) : null}
          {row.eval_pack_href ? (
            <a className="secondary-button action-link-button" href={row.eval_pack_href}>
              Eval pack
            </a>
          ) : null}
          {row.external_artifact_url ? (
            <a className="secondary-button action-link-button" href={row.external_artifact_url} target="_blank" rel="noreferrer">
              ASE artifact
            </a>
          ) : null}
        </ActionCluster>
        <div className="runtime-coverage-provenance">
          <span>Last detected {formatDate(row.last_detected_at)}</span>
          {row.runtime_scenario_review_status ? <span>Review {titleCase(row.runtime_scenario_review_status)}</span> : null}
        </div>
      </div>
    </article>
  );
}

export default function RuntimeRiskCoverageBoardView({ selectedSystem, board, route }) {
  const readiness = proofReadiness(board.summary);
  const currentFilters = {
    workflowId: route.workflowId || null,
    findingStatus: route.findingStatus || null,
    findingPriority: route.findingPriority || null,
    coverageStatus: route.coverageStatus || null,
    attentionOnly: Boolean(route.attentionOnly),
  };

  const navigateWithFilters = (updates) => {
    window.location.hash = buildHref(selectedSystem.id, { ...currentFilters, ...updates });
  };
  const attentionToggleHref = buildHref(selectedSystem.id, {
    ...currentFilters,
    attentionOnly: !currentFilters.attentionOnly,
  });

  return (
    <section className="content-grid single-column">
      <div className="panel detail-panel runtime-coverage-stage-panel">
        <div className="panel-header">
          <div>
            <p className="detail-kicker">Evaluation coverage</p>
            <h2>Runtime reliability coverage</h2>
            <p className="panel-copy">
              Active runtime reliability risks grouped by the latest linked evaluation coverage state.
            </p>
          </div>
          <ActionCluster align="end">
            <a
              className="primary-button action-link-button runtime-coverage-attention-link"
              href={attentionToggleHref}
            >
              {currentFilters.attentionOnly ? 'Show all risks' : 'Needs attention'}
            </a>
            <a className="secondary-button action-link-button" href={buildHref(selectedSystem.id, {})}>
              Clear filters
            </a>
          </ActionCluster>
        </div>

        <div className="metric-grid compact-metrics runtime-coverage-summary-grid">
          <div className="metric-card">
            <span>Active risks</span>
            <strong>{board.summary.active_runtime_risk_count}</strong>
          </div>
          <div className="metric-card">
            <span>Reviewed</span>
            <strong>{board.summary.reviewed_risk_count}</strong>
          </div>
          <div className="metric-card">
            <span>Sent</span>
            <strong>{board.summary.sent_risk_count}</strong>
          </div>
          <div className="metric-card">
            <span>Imported summaries</span>
            <strong>{board.summary.imported_summary_count}</strong>
          </div>
        </div>

        <div className="filter-row runtime-coverage-toolbar">
          <FilterSelect
            label="Workflow"
            value={currentFilters.workflowId}
            options={board.filters.workflow_options || []}
            onChange={(workflowId) => navigateWithFilters({ workflowId })}
          />
          <FilterSelect
            label="Status"
            value={currentFilters.findingStatus}
            options={board.filters.finding_status_options || []}
            onChange={(findingStatus) => navigateWithFilters({ findingStatus })}
          />
          <FilterSelect
            label="Priority"
            value={currentFilters.findingPriority}
            options={board.filters.finding_priority_options || []}
            onChange={(findingPriority) => navigateWithFilters({ findingPriority })}
          />
          <FilterSelect
            label="Coverage"
            value={currentFilters.coverageStatus}
            options={board.filters.coverage_status_options || []}
            onChange={(coverageStatus) => navigateWithFilters({ coverageStatus })}
          />
          <label className="checkbox-control">
            <input
              type="checkbox"
              checked={currentFilters.attentionOnly}
              onChange={(event) => navigateWithFilters({ attentionOnly: event.target.checked })}
            />
            Needs attention
          </label>
        </div>
      </div>

      <div className={`panel detail-panel runtime-coverage-proof-bridge runtime-coverage-proof-bridge-${readiness.tone}`}>
        <SectionHeader
          kicker="Outcome proof"
          title={readiness.label}
          copy={readiness.copy}
        />
        <div className="runtime-coverage-proof-actions">
          <p>{readiness.next}</p>
          <a className="secondary-button action-link-button" href={`#/systems/${selectedSystem.id}/compare`}>
            Open Compare
          </a>
        </div>
      </div>

      {board.groups.length ? (
        board.groups.map((group) => (
          <div className="panel detail-panel runtime-coverage-group" key={group.status}>
            <div className="runtime-coverage-group-header">
              <SectionHeader
                kicker={`${group.count} ${group.count === 1 ? 'risk' : 'risks'}`}
                title={group.label}
                copy={group.summary}
              />
            </div>
            <div className="finding-card-list">
              {group.rows.map((row) => (
                <CoverageRow key={row.finding_id} row={row} />
              ))}
            </div>
          </div>
        ))
      ) : (
        <div className="panel detail-panel">
          <div className="empty-state runtime-coverage-empty">
            <h2>No runtime risks match these filters</h2>
            <p>Adjust the coverage, priority, status, or workflow filters to review other active risks.</p>
            <a className="secondary-button action-link-button" href={buildHref(selectedSystem.id, {})}>
              Show all coverage states
            </a>
          </div>
        </div>
      )}
    </section>
  );
}
